import { api } from '../api';

export type PreviewTicketResponse = Awaited<
  ReturnType<ReturnType<typeof api.api.api.v1.files>['preview-ticket']['post']>
>;

export interface PreviewThrottlerOptions {
  maxConcurrent?: number;
  minIntervalMs?: number;
  backoffCooldownMs?: number;
  cacheTtlMs?: number;
  fetchTicket?: (fileId: string, signal?: AbortSignal) => Promise<PreviewTicketResponse>;
}

export interface RequestPreviewTicketOptions {
  priority?: 'normal' | 'high';
  signal?: AbortSignal;
  skipCache?: boolean;
}

interface QueuedItem {
  fileId: string;
  priority: 'normal' | 'high';
  skipCache: boolean;
  signal?: AbortSignal;
  resolve: (value: PreviewTicketResponse) => void;
  reject: (reason?: unknown) => void;
  onAbort?: () => void;
}

interface CacheEntry {
  response: PreviewTicketResponse;
  expiresAt: number;
}

export class PreviewThrottler {
  private maxConcurrent: number;
  private minIntervalMs: number;
  private backoffCooldownMs: number;
  private cacheTtlMs: number;
  private fetchTicket: (fileId: string, signal?: AbortSignal) => Promise<PreviewTicketResponse>;

  private queue: QueuedItem[] = [];
  private inFlight = new Map<string, Promise<PreviewTicketResponse>>();
  private cache = new Map<string, CacheEntry>();
  private activeCount = 0;
  private lastDispatchedAt = 0;
  private backoffUntil = 0;
  private scheduleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: PreviewThrottlerOptions = {}) {
    this.maxConcurrent = options.maxConcurrent ?? 3;
    this.minIntervalMs = options.minIntervalMs ?? 120;
    this.backoffCooldownMs = options.backoffCooldownMs ?? 5000;
    this.cacheTtlMs = options.cacheTtlMs ?? 5 * 60 * 1000; // 5 minutes default
    this.fetchTicket =
      options.fetchTicket ??
      ((fileId: string) => api.api.api.v1.files({ id: fileId })['preview-ticket'].post());
  }

  get queueLength(): number {
    return this.queue.length;
  }

  get activeRequests(): number {
    return this.activeCount;
  }

  get isBackingOff(): boolean {
    return Date.now() < this.backoffUntil;
  }

  request(
    fileId: string,
    options: RequestPreviewTicketOptions = {},
  ): Promise<PreviewTicketResponse> {
    const { priority = 'normal', signal, skipCache = false } = options;

    if (signal?.aborted) {
      return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }

    // 1. Check valid cached response
    if (!skipCache) {
      const cached = this.getCached(fileId);
      if (cached) {
        return Promise.resolve(cached);
      }
    }

    // 2. Check deduplication for currently in-flight request for identical fileId
    const existingInFlight = this.inFlight.get(fileId);
    if (existingInFlight) {
      if (!signal) {
        return existingInFlight;
      }
      return new Promise<PreviewTicketResponse>((resolve, reject) => {
        const onAbort = () => {
          signal.removeEventListener('abort', onAbort);
          reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort);
        existingInFlight
          .then((res) => {
            signal.removeEventListener('abort', onAbort);
            resolve(res);
          })
          .catch((err) => {
            signal.removeEventListener('abort', onAbort);
            reject(err);
          });
      });
    }

    // 3. Enqueue the request
    return new Promise<PreviewTicketResponse>((resolve, reject) => {
      const queuedItem: QueuedItem = {
        fileId,
        priority,
        skipCache,
        signal,
        resolve,
        reject,
      };

      if (signal) {
        const onAbort = () => {
          this.removeQueuedItem(queuedItem);
          reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
        };
        queuedItem.onAbort = onAbort;
        signal.addEventListener('abort', onAbort, { once: true });
      }

      if (priority === 'high') {
        const firstNormalIndex = this.queue.findIndex((item) => item.priority === 'normal');
        if (firstNormalIndex === -1) {
          this.queue.push(queuedItem);
        } else {
          this.queue.splice(firstNormalIndex, 0, queuedItem);
        }
      } else {
        this.queue.push(queuedItem);
      }

      this.scheduleQueue();
    });
  }

  invalidate(fileId: string): void {
    this.cache.delete(fileId);
  }

  clearCache(): void {
    this.cache.clear();
  }

  private getCached(fileId: string): PreviewTicketResponse | null {
    const entry = this.cache.get(fileId);
    if (!entry) return null;
    if (Date.now() >= entry.expiresAt) {
      this.cache.delete(fileId);
      return null;
    }
    return entry.response;
  }

  private removeQueuedItem(item: QueuedItem): void {
    const index = this.queue.indexOf(item);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  }

  private scheduleQueue(): void {
    if (this.queue.length === 0) {
      return;
    }

    if (this.activeCount >= this.maxConcurrent) {
      return;
    }

    const now = Date.now();
    let delay = 0;

    if (now < this.backoffUntil) {
      delay = Math.max(delay, this.backoffUntil - now);
    }

    const elapsedSinceLastDispatch = now - this.lastDispatchedAt;
    if (elapsedSinceLastDispatch < this.minIntervalMs) {
      delay = Math.max(delay, this.minIntervalMs - elapsedSinceLastDispatch);
    }

    if (delay === 0) {
      if (this.scheduleTimer !== null) {
        clearTimeout(this.scheduleTimer);
        this.scheduleTimer = null;
      }
      this.processQueue();
      return;
    }

    if (this.scheduleTimer !== null) {
      return;
    }

    this.scheduleTimer = setTimeout(() => {
      this.scheduleTimer = null;
      this.processQueue();
    }, delay);
  }

  private processQueue(): void {
    if (this.queue.length === 0) {
      return;
    }

    const now = Date.now();

    // If still in backoff, reschedule
    if (now < this.backoffUntil) {
      this.scheduleQueue();
      return;
    }

    // Check concurrency limit
    if (this.activeCount >= this.maxConcurrent) {
      return;
    }

    // Check spacing interval
    const elapsedSinceLast = now - this.lastDispatchedAt;
    if (elapsedSinceLast < this.minIntervalMs) {
      this.scheduleQueue();
      return;
    }

    // Dequeue next job
    const item = this.queue.shift();
    if (!item) {
      return;
    }

    if (item.signal) {
      if (item.onAbort) {
        item.signal.removeEventListener('abort', item.onAbort);
      }
      if (item.signal.aborted) {
        item.reject(item.signal.reason ?? new DOMException('Aborted', 'AbortError'));
        this.scheduleQueue();
        return;
      }
    }

    // Check cache again in case a prior in-flight request fulfilled it while waiting
    if (!item.skipCache) {
      const cached = this.getCached(item.fileId);
      if (cached) {
        item.resolve(cached);
        this.scheduleQueue();
        return;
      }
    }

    this.activeCount++;
    this.lastDispatchedAt = Date.now();

    const fetchPromise = (async () => {
      try {
        return await this.executeFetch(item.fileId, item.signal);
      } finally {
        this.inFlight.delete(item.fileId);
        this.activeCount--;
        this.scheduleQueue();
      }
    })();
    this.inFlight.set(item.fileId, fetchPromise);

    fetchPromise
      .then((res) => {
        item.resolve(res);
      })
      .catch((err) => {
        item.reject(err);
      });

    // If we have capacity and more items, schedule next step immediately
    if (this.queue.length > 0 && this.activeCount < this.maxConcurrent) {
      this.scheduleQueue();
    }
  }

  private async executeFetch(fileId: string, signal?: AbortSignal): Promise<PreviewTicketResponse> {
    try {
      const response = await this.fetchTicket(fileId, signal);

      // Check for rate limit or server saturation
      if (response.status === 429 || response.status === 503) {
        this.triggerBackoff();
      } else if (response.error && typeof response.error === 'object' && response.error !== null) {
        const errObj = response.error as Record<string, unknown>;
        if (errObj.status === 429 || errObj.status === 503) {
          this.triggerBackoff();
        }
      }

      // Cache successful response
      if (!response.error && response.data) {
        let ttl = this.cacheTtlMs;
        if (response.data.expiresAt) {
          const expiresTimestamp = new Date(response.data.expiresAt).getTime();
          if (!Number.isNaN(expiresTimestamp)) {
            const timeToExpiry = expiresTimestamp - Date.now();
            if (timeToExpiry > 0) {
              ttl = Math.min(ttl, timeToExpiry);
            }
          }
        }
        this.cache.set(fileId, {
          response,
          expiresAt: Date.now() + ttl,
        });
      }

      return response;
    } catch (err) {
      const status =
        typeof err === 'object' && err !== null && 'status' in err && typeof err.status === 'number'
          ? err.status
          : undefined;

      if (status === 429 || status === 503) {
        this.triggerBackoff();
      }
      throw err;
    }
  }

  private triggerBackoff(): void {
    this.backoffUntil = Date.now() + this.backoffCooldownMs;
    this.scheduleQueue();
  }
}

export const previewThrottler = new PreviewThrottler();

export function requestPreviewTicket(
  fileId: string,
  options?: RequestPreviewTicketOptions,
): Promise<PreviewTicketResponse> {
  return previewThrottler.request(fileId, options);
}

export function invalidatePreviewTicket(fileId: string): void {
  previewThrottler.invalidate(fileId);
}
